import axios from 'axios';

const API_BASE = '/api';

// Helper to get auth token from cookie
function getAuthToken(): string | null {
  const match = document.cookie.match(/sunsip_token=([^;]+)/);
  return match ? match[1] : null;
}

// Helper to check if we have a token (for auth state)
export function hasAuthToken(): boolean {
  return !!getAuthToken();
}

// ============ AUTH ============

export async function signUp(email: string, password: string): Promise<{ user: { id: string; email: string; isAdmin: boolean } | null; error: { message: string } | null }> {
  try {
    const response = await axios.post(`${API_BASE}/auth-signup`, { email, password });
    return { user: response.data.user, error: null };
  } catch (error: any) {
    return { user: null, error: { message: error.response?.data?.error || 'Sign up failed' } };
  }
}

export async function signIn(email: string, password: string): Promise<{ user: { id: string; email: string; isAdmin: boolean } | null; error: { message: string } | null }> {
  try {
    const response = await axios.post(`${API_BASE}/auth-signin`, { email, password });
    return { user: response.data.user, error: null };
  } catch (error: any) {
    return { user: null, error: { message: error.response?.data?.error || 'Sign in failed' } };
  }
}

export async function signOut(): Promise<void> {
  try {
    await axios.post(`${API_BASE}/auth-signout`);
  } catch {
    // Ignore errors
  }
}

// ============ PASSWORD RESET ============

export interface ForgotPasswordResult {
  message: string;
  /** Present only when the email service is not configured yet (demo fallback). */
  resetUrl?: string;
}

export async function forgotPassword(email: string): Promise<ForgotPasswordResult> {
  const response = await axios.post(`${API_BASE}/auth-forgot`, { email });
  return {
    message: response.data.message || 'If that email exists, a reset link is on its way.',
    resetUrl: response.data.resetUrl,
  };
}

export async function resetPassword(token: string, password: string): Promise<{ user: { id: string; email: string; isAdmin: boolean } | null; error: { message: string } | null }> {
  try {
    const response = await axios.post(`${API_BASE}/auth-reset`, { token, password });
    return { user: response.data.user, error: null };
  } catch (error: any) {
    return { user: null, error: { message: error.response?.data?.error || 'Password reset failed' } };
  }
}

export async function getCurrentUser(): Promise<{ id: string; email: string; isAdmin: boolean } | null> {
  try {
    const response = await axios.get(`${API_BASE}/auth-me`);
    return response.data.user || null;
  } catch {
    return null;
  }
}

// ============ SAVED COMBINATIONS ============

export interface ApiSavedCombination {
  id: string;
  userId: string;
  cityName: string;
  countryName: string;
  cityImageUrl: string;
  weatherDetails: string;
  cocktailName: string;
  cocktailImageUrl: string;
  cocktailIngredients: string[];
  cocktailRecipe: string[];
  rating?: number;
  notes: string;
  timesAccessed: number;
  lastAccessedAt?: string;
  createdAt: string;
}

/** Mongo returns `_id`; the whole FE works with `id`. Normalize at the edge:
 *  `id` from `_id`, `savedAt` mirrors createdAt, nullable DB fields get
 *  FE-friendly defaults. */
function normalizeCombination(raw: any): ApiSavedCombination & { savedAt: string } {
  const { _id, ...rest } = raw ?? {};
  return {
    id: raw?.id ?? _id,
    cityName: raw?.cityName ?? '',
    countryName: raw?.countryName ?? '',
    cityImageUrl: raw?.cityImageUrl ?? '',
    weatherDetails: raw?.weatherDetails ?? '',
    cocktailName: raw?.cocktailName ?? '',
    cocktailImageUrl: raw?.cocktailImageUrl ?? '',
    cocktailIngredients: Array.isArray(raw?.cocktailIngredients) ? raw.cocktailIngredients : [],
    cocktailRecipe: Array.isArray(raw?.cocktailRecipe) ? raw.cocktailRecipe : [],
    notes: raw?.notes ?? '',
    rating: typeof raw?.rating === 'number' ? raw.rating : undefined,
    lastAccessedAt: raw?.lastAccessedAt ?? undefined,
    savedAt: raw?.savedAt ?? raw?.createdAt,
    ...rest,
  } as unknown as ApiSavedCombination & { savedAt: string };
}

export async function saveCombination(userId: string, data: {
  cityName: string;
  countryName: string;
  cityImageUrl: string;
  weatherDetails: string;
  cocktailName: string;
  cocktailImageUrl: string;
  cocktailIngredients: string[];
  cocktailRecipe: string[];
  rating?: number;
  notes?: string;
}) {
  const response = await axios.post(`${API_BASE}/combinations`, data);
  return normalizeCombination(response.data.combination ?? response.data);
}

export async function getUserSavedCombinations(userId: string) {
  const response = await axios.get(`${API_BASE}/combinations`);
  const list = response.data.combinations ?? response.data ?? [];
  return (Array.isArray(list) ? list : []).map(normalizeCombination);
}

export async function updateCombinationRating(id: string, rating: number, notes?: string) {
  await axios.patch(`${API_BASE}/combination?id=${id}`, { rating, notes });
}

export async function trackCombinationAccess(id: string) {
  try {
    await axios.patch(`${API_BASE}/combination?id=${id}`, { trackAccess: true });
  } catch (error) {
    console.error('Error tracking combination access:', error);
  }
}

export async function getUserTopCombinations(userId: string, limit: number = 5) {
  const response = await axios.get(`${API_BASE}/combinations?top=true&limit=${limit}`);
  return response.data;
}

export async function deleteSavedCombination(id: string) {
  await axios.delete(`${API_BASE}/combination?id=${id}`);
}

// ============ USER PREFERENCES ============

export async function getUserPreferences(userId: string) {
  const response = await axios.get(`${API_BASE}/preferences`);
  return response.data;
}

export async function saveUserPreferences(userId: string, preferences: {
  preferredSpirits: string[];
  dietaryRestrictions: string[];
  favoriteWeatherMoods: Record<string, any>;
}) {
  const response = await axios.put(`${API_BASE}/preferences`, preferences);
  return response.data;
}

// ============ REQUEST LIMITS ============

export async function checkAndUpdateRequestLimit(userId: string | null, clientId?: string | null) {
  try {
    const response = await axios.post(`${API_BASE}/request-limit`, {
      userId,
      clientId,
    });
    return response.data;
  } catch (error) {
    console.error('Error checking request limit:', error);
    // Default to allowing the request if there's an error
    return {
      canProceed: true,
      count: 0,
      remaining: 10,
      resetDate: null,
    };
  }
}

// ============ SYSTEM SETTINGS ============

export async function getSystemSettings() {
  try {
    const response = await axios.get(`${API_BASE}/system-settings`);
    return response.data;
  } catch {
    return { global_enabled: true };
  }
}